import { useState } from 'react'

import { type AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Flex,
  HStack,
  TertiaryButton,
} from '@enechain/ecloud-designsystem'
import {
  FaArrowUpFromBracketRegular,
  FaPlusRegular,
} from '@enechain/ecloud-icons'

import { PageScrollArea } from '~/components/common/PageScrollArea'
import { useErrorToast } from '~/hooks/useErrorToast'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { type CivilDate, civilDateToProtoDate } from '~/models/civilDate'
import { CreateNoticesDialog } from '~/pages/SplitSupplyPlan/components/CreateNoticesDialog'
import { ImportOwnNoticesDialog } from '~/pages/SplitSupplyPlan/components/ImportOwnNoticesDialog'
import { SplitSupplyNoticeTable } from '~/pages/SplitSupplyPlan/components/SplitSupplyNoticeTable'
import { useCreateOwnSplitSupplyNotices } from '~/pages/SplitSupplyPlan/hooks/useCreateOwnSplitSupplyNotices'
import { useDownloadOwnSplitSupplyNoticeXml } from '~/pages/SplitSupplyPlan/hooks/useDownloadOwnSplitSupplyNoticeXml'
import { useListOwnSplitSupplyNotices } from '~/pages/SplitSupplyPlan/hooks/useListOwnSplitSupplyNotices'
import {
  SECTION_OF_TAB,
  type SectionTab,
} from '~/pages/SplitSupplyPlan/models/period'
import { describeSplitSupplyNoticeError } from '~/pages/SplitSupplyPlan/models/serverError'

type Props = {
  tab: SectionTab
  target: CivilDate
  onTargetChange: (target: CivilDate) => void
  area: AreaCode | undefined
  today: CivilDate
}

/**
 * 自社の通告値 (自社が通告値を出す契約の分)。 1 行 = 電文 1 通。 「計画作成」 で通告値を作り、
 * 「取込」 でファイル (XML・ZIP・CSV) から作る・上書きし、 行から詳細 (48 コマの確認・編集・保存) と
 * XML ダウンロードへ進む。 広域機関への提出は含まない。
 * 断面・対象日・エリアは上の絞り込み (`SectionFilterBar`) が決める。
 */
export const OwnNoticesView = ({
  tab,
  target,
  onTargetChange,
  area,
  today,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  const showErrorToast = useErrorToast()
  const [isCreateOpen, setCreateOpen] = useState(false)
  const [isImportOpen, setImportOpen] = useState(false)

  const { data, isLoading, isError, refetch } = useListOwnSplitSupplyNotices({
    section: SECTION_OF_TAB[tab],
    targetDate: target,
    areaCode: area,
  })
  const { mutate: createNotices, isPending: isCreating } =
    useCreateOwnSplitSupplyNotices()
  const { download } = useDownloadOwnSplitSupplyNoticeXml()

  const handleCreate = (input: {
    targetDate: CivilDate
    areaCode: AreaCode
  }): void => {
    createNotices(
      {
        section: SECTION_OF_TAB[tab],
        targetDate: civilDateToProtoDate(input.targetDate),
        areaCode: input.areaCode,
      },
      {
        onSuccess: (res) => {
          setCreateOpen(false)
          // 作った対象日の一覧を見せる
          onTargetChange(input.targetDate)
          // 作成済みの地点も無い時は、対象の契約が無い (AC-S8)
          if (res.createdCount === 0 && res.existingCount === 0) {
            showErrorToast({
              title: t('page:splitSupplyPlan.createDialog.noContract'),
            })
            return
          }
          showSuccessToast({
            title:
              res.createdCount > 0
                ? t('page:splitSupplyPlan.createDialog.successTitle', {
                    count: res.createdCount,
                  })
                : t('page:splitSupplyPlan.createDialog.nothingCreated'),
          })
        },
        onError: (err) => {
          showErrorToast({
            title: t('page:splitSupplyPlan.createDialog.errorTitle'),
            description: describeSplitSupplyNoticeError(err, t),
          })
        },
      },
    )
  }

  return (
    <>
      <Flex alignItems="center" justifyContent="flex-end" gap={2}>
        <TertiaryButton
          onClick={() => {
            setImportOpen(true)
          }}
          size="md"
        >
          <HStack gap={1}>
            <FaArrowUpFromBracketRegular />
            <span>{t('page:splitSupplyPlan.own.importButton')}</span>
          </HStack>
        </TertiaryButton>
        <TertiaryButton
          onClick={() => {
            setCreateOpen(true)
          }}
          size="md"
        >
          <HStack gap={1}>
            <FaPlusRegular />
            <span>{t('page:splitSupplyPlan.createButton')}</span>
          </HStack>
        </TertiaryButton>
      </Flex>

      {data === undefined && isError ? (
        <Alert
          description={
            <Flex alignItems="center" gap={3}>
              <span>{t('page:splitSupplyPlan.error.fetchFailed')}</span>
              <TertiaryButton
                onClick={() => {
                  void refetch()
                }}
                size="sm"
              >
                {t('page:splitSupplyPlan.error.retry')}
              </TertiaryButton>
            </Flex>
          }
          status="error"
        />
      ) : (
        <PageScrollArea>
          <SplitSupplyNoticeTable
            isLoading={isLoading}
            onDownload={(item, format) => {
              if (item.key !== undefined) void download(item.key, format)
            }}
            rows={data?.items ?? []}
          />
        </PageScrollArea>
      )}

      <CreateNoticesDialog
        // 開くたびに対象日・エリアの初期値 (一覧の現在の選択) から始める
        key={`${isCreateOpen}-${tab}`}
        initialArea={area}
        initialTarget={target}
        isCreating={isCreating}
        isOpen={isCreateOpen}
        onClose={() => {
          setCreateOpen(false)
        }}
        onCreate={handleCreate}
        tab={tab}
        today={today}
      />
      <ImportOwnNoticesDialog
        // 開くたびに選んだファイルと確認の状態を空から始める
        key={`import-${isImportOpen}-${tab}`}
        area={area}
        isOpen={isImportOpen}
        onClose={() => {
          setImportOpen(false)
        }}
        tab={tab}
        target={target}
        today={today}
      />
    </>
  )
}
